import { Component, Input, } from '@angular/core';
import { Chargepoint } from '../../../api/chargepoints-api.service';
import { Trace, TraceSearchResult, TracesApiService } from '../../../api/traces-api.service';
import { RefreshService } from '../../common/refresh-widget/refresh.service';
import { catchError, debounceTime, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { JsonViewerService } from '../../common/json-viewer-dialog/json-viewer.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

const COLOR_OK = "#4CAF50";
const COLOR_PRIMARY = "#FF9800";
const COLOR_WARNING = "#FF5722";
const COLOR_ERROR = "darkred";
const COLOR_INFO = "#2196F3";
const COLOR_DISABLED = "#9E9E9E";

const TRACE_NAME_COLOR_MAP = {
  [COLOR_OK]: ["WebsocketAuthentication", "BootNotification", "WebsocketNewSessionStart"],
  [COLOR_PRIMARY]: ["RemoteStartTransaction", "StartTransaction", "MeterValues"],
  [COLOR_INFO]: ["StatusNotification", "Heartbeat"],
  [COLOR_WARNING]: ["RemoteStopTransaction", "StopTransaction"],
  [COLOR_ERROR]: ["WebsocketSessionStop", "RemoteReset"]
}

@Component({
  selector: 'app-chargepoint-traces',
  templateUrl: './chargepoint-traces.component.html',
  styleUrls: ['./chargepoint-traces.component.scss'],
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    MatCardModule,
    MatProgressBarModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatPaginatorModule,
    MatExpansionModule,
    NgxJsonViewerModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule
  ]
})
export class ChargepointTracesComponent {

  @Input() chargepoint!: Chargepoint;

  public traces$: Observable<Trace[]>;
  public total$: Observable<number>;
  public loading = false;
  public fetchingError = false;

  public page$ = new BehaviorSubject<PageEvent>({ pageIndex: 0, pageSize: 10, length: 0 });
  readonly dateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  constructor(private tracesApiService: TracesApiService, private refreshService: RefreshService, public jsonViewerService: JsonViewerService) {
    const range$ = this.dateRange.valueChanges.pipe(
      startWith(this.dateRange.value),
      tap(() => {
        if (this.page$.value.pageIndex !== 0) {
          this.page$.next({ ...this.page$.value, pageIndex: 0 });
        }
      })
    );

    const result$ = combineLatest([
      this.refreshService.refreshTrigger.pipe(startWith(undefined)),
      range$,
      this.page$
    ]).pipe(
      debounceTime(0),
      filter(() => !!this.chargepoint),
      tap(() => {
        this.loading = true;
        this.fetchingError = false;
      }),
      switchMap(([_, range, page]) => {
        const end = range.end ? new Date(range.end.getTime() + 24 * 60 * 60 * 1000) : undefined;
        return this.tracesApiService.searchTraces({
          chargepointId: this.chargepoint.id,
          from: range.start ?? undefined,
          to: end,
          page: page.pageIndex,
          size: page.pageSize,
        }).pipe(
          catchError(() => {
            this.fetchingError = true;
            return of({ traces: [], total: 0 } as TraceSearchResult);
          })
        );
      }),
      tap(() => {
        this.loading = false;
      }),
      map(result => ({
        total: result.total,
        traces: result.traces.map(trace => {
          const splittedName = trace.name.split('.');
          return {
            ...trace,
            name: splittedName.length ? splittedName[splittedName.length - 1] : 'missing name...'
          };
        })
      })),
      shareReplay(1)
    );

    this.traces$ = result$.pipe(map(result => result.traces));
    this.total$ = result$.pipe(map(result => result.total));
  }

  public onPageChange(event: PageEvent) {
    this.page$.next(event);
  }

  public getTraceBGColor(trace: Trace) {
    for (const [color, names] of Object.entries(TRACE_NAME_COLOR_MAP)) {
      if (names.includes(trace.name)) {
        return color;
      }
    }
    return "lightgrey";
  }

  public getTraceColor(trace: Trace) {
    return this.getTraceBGColor(trace) === 'lightgrey' ? 'black' : 'white';
  }
}
